import * as React from 'react'
import { useMemo } from 'react'
import cn from 'classnames'
import last from 'lodash/last'
import nth from 'lodash/nth'
import Link from 'next/link'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { ITaxonomyWithoutChildren } from '@core/api/Categories/types'
import {
  LYSBreadcrumb,
  LYSIcon,
  LYSTypography,
} from '@core/components/Primitives'
import routes from '@core/config/routes'
import { usePageContext } from '@core/utils/pages/PageContext'
import style from './index.module.less'

interface Props {
  breadcrumbs?: ITaxonomyWithoutChildren[]
  isProductDetailPage?: boolean
  invert?: boolean
}

const Mobile: React.FC<{
  category: ITaxonomyWithoutChildren
  parent?: ITaxonomyWithoutChildren
  isProductDetailPage?: boolean
}> = ({ category, parent, isProductDetailPage }) => {
  const displayCategory = isProductDetailPage || !parent ? category : parent
  const taxonomyRoute = routes.taxonomy(displayCategory)

  return (
    <LYSBreadcrumb>
      <LYSBreadcrumb.Item>
        <Link {...taxonomyRoute}>
          <>
            {parent && <LYSIcon component={LeftOutlined} />}{' '}
            {displayCategory.name}
          </>
        </Link>
      </LYSBreadcrumb.Item>
    </LYSBreadcrumb>
  )
}

const Desktop: React.FC<{ breadcrumbs: ITaxonomyWithoutChildren[] }> = ({
  breadcrumbs,
}) => {
  return (
    <div className={style.sectionWrap}>
      {breadcrumbs.map((category) => {
        const taxonomyRoute = routes.taxonomy(category)
        return (
          <div className={style.breadcrumbWrap} key={category.id}>
            <LYSIcon component={RightOutlined} className={style.separator} />
            <LYSTypography.Text className={style.breadcrumb}>
              <Link href={taxonomyRoute.href} as={taxonomyRoute.as}>
                {category.name}
              </Link>
            </LYSTypography.Text>
          </div>
        )
      })}
    </div>
  )
}

const CategoryBreadcrumbs: React.FC<Props> = ({
  breadcrumbs,
  isProductDetailPage,
  invert,
}) => {
  const {
    props: { isMobile },
  } = usePageContext()

  // Removes the first breadcrumb from our breadcrumb list
  const visibleBreadcrumbs = useMemo(() => {
    const result = (breadcrumbs || []).slice()
    result.shift()

    return result
  }, [breadcrumbs])
  const current = last(visibleBreadcrumbs)
  const parent = nth(visibleBreadcrumbs, -2)

  if (!breadcrumbs || !current) return null

  return (
    <div className={cn(style.breadcrumbs, invert && style.invert)}>
      {isMobile ? (
        <Mobile
          category={current}
          parent={parent}
          isProductDetailPage={isProductDetailPage}
        />
      ) : (
        <Desktop breadcrumbs={visibleBreadcrumbs} />
      )}
    </div>
  )
}

export default CategoryBreadcrumbs
